import { useCallback, useState, useRef, useEffect } from 'react';

import { Grid } from 'antd';

import { useSectionScroll } from '@/hooks/useSectionScroll';
import { cn } from '@/lib/cn';
import { type Step } from '@/stores/steps';

export type SelectionOption = {
  title: string;
  subtitle?: string;
  itemKey: string;
  steps?: Step[];
  redirectTo?: string;
  disabled?: boolean;
  icon: React.JSXElementConstructor<{ className: string }>;
};

type ProductSelectorProps = {
  list: SelectionOption[];
  onSelect?: (selected: SelectionOption) => void;
  selectedKey?: string;
};

const CARD_OVERLAP_FACTOR = 0.25;
const VERTICAL_OFFSET_FACTOR = 0.2;

const ProductSelector = ({ list, onSelect, selectedKey }: ProductSelectorProps) => {
  const [selected, setSelected] = useState<string>(selectedKey || '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const screens = Grid.useBreakpoint();

  const scrollToItem = useCallback(
    (item: SelectionOption) => {
      const element = itemRefs.current[item.itemKey];

      if (!element || !scrollContainerRef.current || !wrapperRef.current) {
        return;
      }

      const containerDimensions = scrollContainerRef.current.getBoundingClientRect();

      const itemIndex = list.findIndex(listItem => listItem.itemKey === item.itemKey);
      const itemHeight = element.offsetHeight;

      const elementTopPosition = list.slice(0, itemIndex).reduce(heightSum => {
        const itemHeightWithoutMargin = itemHeight - itemHeight * CARD_OVERLAP_FACTOR;
        return heightSum + itemHeightWithoutMargin;
      }, 0);

      const containerCenter = containerDimensions.height / 2;
      const elementCenter = itemHeight / 2;

      const threshold = containerDimensions.height * VERTICAL_OFFSET_FACTOR;
      const translateY = containerCenter - elementCenter - elementTopPosition - threshold;

      wrapperRef.current.style.transform = `translateY(${translateY}px)`;
    },
    [list]
  );

  const handleSelection = useCallback(
    (item: SelectionOption) => {
      setSelected(item.itemKey);
      onSelect?.(item);
      scrollToItem(item);
    },
    [onSelect, scrollToItem]
  );

  useEffect(() => {
    handleSelection(list.find(item => item.itemKey === selectedKey) || list[0]);
  }, [selectedKey, handleSelection, list]);

  const handleScroll = useCallback(
    (direction: 'down' | 'up') => {
      const currentIndex = list.findIndex(item => item.itemKey === selected);

      if (direction === 'down' && currentIndex > 0) {
        const previousItem = list[currentIndex - 1];
        handleSelection(previousItem);
        return;
      }

      if (direction === 'up' && currentIndex < list.length - 1) {
        const nextItem = list[currentIndex + 1];
        handleSelection(nextItem);
      }
    },
    [selected, handleSelection, list]
  );

  useSectionScroll(handleScroll, screens.lg);

  if (screens.lg) {
    return (
      <div className='mx-auto mb-30 flex max-h-fit max-w-197.5 flex-1 flex-wrap justify-center gap-8 transition-transform'>
        {list.map((item, index, items) => {
          const isSelected = selected === item.itemKey;

          return (
            <div
              key={`product-${item.itemKey}`}
              id={`product-item-${index}`}
              tabIndex={0}
              style={{ zIndex: items.length - index }}
              ref={element => {
                itemRefs.current[item.itemKey] = element;
              }}
              className={cn(
                'bg-surface-default border-outline-disabled flex max-h-76.25 w-60.5 cursor-pointer flex-col items-center rounded-xl border border-solid px-6 py-14 shadow-md transition-all duration-300 select-none hover:bg-slate-50 dark:hover:bg-neutral-800',
                {
                  'border-primary z-50! border-2': isSelected,
                  'border-neutral-300': isSelected && item.disabled
                }
              )}
              onClick={() => handleSelection(item)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelection(item);
                }
              }}
            >
              <span className='dark:bg-secondary-50/10 mb-6! flex items-center justify-center rounded-full bg-sky-100 p-4'>
                <item.icon className='text-primary h-10 w-10' />
              </span>

              <strong className='text-primary text-center text-lg'>{item.title}</strong>
              {item.subtitle && (
                <strong className='text-primary text-center text-lg font-normal!'>{item.subtitle}</strong>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className='flex flex-1 flex-col items-center overflow-y-hidden transition-transform'>
      <div
        ref={wrapperRef}
        className='flex flex-1 flex-col transition-transform duration-300 ease-[cubic-bezier(0.38,1.3,0.68,1)]'
      >
        {list.map((item, index, items) => {
          const isSelected = selected === item.itemKey;

          return (
            <div
              key={`product-${item.itemKey}`}
              id={`product-item-${index}`}
              tabIndex={0}
              style={{ zIndex: items.length - index }}
              ref={element => {
                itemRefs.current[item.itemKey] = element;
              }}
              className={cn(
                'bg-surface-default border-outline-disabled flex h-76 w-60.5 cursor-pointer flex-col items-center justify-center rounded-xl border border-solid px-6 py-14 shadow-md transition-all duration-300 select-none hover:bg-slate-50 dark:hover:bg-neutral-800',
                {
                  'border-primary z-50! border-2': isSelected,
                  'mt-[-25%] odd:rotate-6 even:-rotate-6': !isSelected,
                  'border-neutral-300': isSelected && item.disabled
                }
              )}
              onClick={() => handleSelection(item)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelection(item);
                }
              }}
            >
              <span className='dark:bg-secondary-50/10 mb-6! flex items-center justify-center rounded-full bg-sky-100 p-4'>
                <item.icon className='text-primary h-10 w-10' />
              </span>

              <strong className='text-primary text-center text-lg'>{item.title}</strong>
              {item.subtitle && (
                <strong className='text-primary text-center text-lg font-normal!'>{item.subtitle}</strong>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductSelector;
